import { stripHtml, toNumber } from './reportGridUtils'

/**
 * One cell of a report, as an EXPORT writes it (spec 133).
 *
 * Every view builder — the grouped grid's (`groupedExportView`), the flat grid's
 * (`ReportDataGrid`) and the queue drawer's (`jobResultView`) — turns CB's HTML
 * `displayValue` into export text through here, so there is one converter and no
 * per-page copies.
 *
 * ## Why this exists
 *
 * `stripHtml` deletes tags and inserts nothing in their place. AP Detail's
 * Vendor / Bill cell is two blocks — `<div>Kayveo</div><div>82</div>`, the vendor
 * over the bill number, as the screen shows it — and every client export printed
 * "Kayveo82": 59 of 59 lines on the live As Of 09/28/2026 run. Trial Balance runs
 * the account number into the name the same way (fixed at its source, see
 * TrialBalanceReport.jsx), which is not a block tag and so not this module's job.
 *
 * ## What it does
 *
 *  - A `<br>` or a CLOSING div / p / li / tr / h1-h6 tag ends a line: the text is
 *    stripped, each line trimmed, empty lines dropped, joined with "\n".
 *  - On EVERY branch a CR LF / lone CR becomes "\n" and a leading or trailing line
 *    break is dropped — CB's own text can carry them (report 137's live
 *    `"Admin Fees\r\n"` must export as "Admin Fees", not as a cell with a blank
 *    second line).
 *  - Anything else is `stripHtml(html)` byte for byte, so a single-line cell is
 *    exactly what every export wrote before (pinned over every displayValue in the
 *    report 44 and 222 captures).
 *
 * The line break is kept for the VISUAL formats — the PDF (winAnsiSafe keeps
 * \x0A, autotable prints one line per "\n") and the XLSX (wrapText) — and folded
 * to a single space for the LINE-oriented ones by `singleLine`: the client CSV and
 * the AI payload.
 */

// Tags that end a line of text. Opening tags do not: `<div>a</div><div>b</div>`
// ends "a" once, not twice.
const LINE_END_TAG = /<br\s*\/?>|<\/(?:div|p|li|tr|h[1-6])\s*>/gi
const HAS_LINE_END_TAG = /<br\s*\/?>|<\/(?:div|p|li|tr|h[1-6])\s*>/i
const HAS_BREAK = /[\r\n]/

const normaliseBreaks = (s) => s.replace(/\r\n?/g, '\n')
// A leading/trailing line break, with any whitespace hugging it.
const trimOuterBreaks = (s) => s.replace(/^\s*\n/, '').replace(/\n\s*$/, '')

/** A cell's display HTML as export text (see the module note). */
export const exportText = (html) => {
  const raw = String(html ?? '')
  if (HAS_LINE_END_TAG.test(raw)) {
    const text = normaliseBreaks(stripHtml(raw.replace(LINE_END_TAG, '\n')))
    return text.split('\n').map((line) => line.trim()).filter((line) => line !== '').join('\n')
  }
  const text = stripHtml(raw)
  if (!HAS_BREAK.test(text)) return text // today's output, byte for byte
  return trimOuterBreaks(normaliseBreaks(text))
}

/**
 * An exported cell: a REAL number for a numeric column (CC-49370 — Excel totals
 * must sum), read from CB's raw `dataValue` when it has one and from the display
 * otherwise, exactly as the grids' own math (`cellNumber`) reads it; the export
 * text when the value is not a number or the column is not numeric.
 *
 * @param display CB's displayValue (HTML)
 * @param raw     CB's dataValue, or undefined where the caller has none (footers)
 * @param numeric the column's classification (`isNumericColumn` + the footer fallback)
 */
export const exportCell = (display, raw, numeric) => {
  const text = exportText(display)
  if (!numeric) return text
  const n = toNumber(raw != null ? raw : display)
  return n != null ? n : text
}

/**
 * A multi-line export value folded onto one line (" "), for the line-oriented
 * formats: the client CSV (one record per line for a system import) and the AI
 * payload (cb-api joins a row's cells into ONE prompt line). Anything that is not
 * a string, or has no line break, passes through untouched.
 */
export const singleLine = (v) => (typeof v === 'string' && HAS_BREAK.test(v) ? v.replace(/\s*[\r\n]\s*/g, ' ') : v)

export default exportCell
